import { create } from "zustand";
import { persist } from "zustand/middleware";
import { User, LoginCredentials, SignupCredentials } from "@/types/user.types";
import { authApi } from "@/services/api/endpoints/auth.api";

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
}

interface AuthActions {
  login: (credentials: LoginCredentials) => Promise<void>;
  register: (credentials: SignupCredentials) => Promise<void>;
  logout: () => Promise<void>;
  getCurrentUser: () => Promise<void>;
  clearError: () => void;
  setUser: (user: User | null) => void;
  hydrateAuth: () => Promise<boolean>;
  loginWithGoogle: (token: string) => Promise<boolean>;
}

type AuthStore = AuthState & AuthActions;

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // State
      user: null,
      token: null,
      isAuthenticated: false,
      loading: true, // Start with loading true until hydration completes
      error: null,

      // Actions
      login: async (credentials: LoginCredentials) => {
        set({ loading: true, error: null });
        try {
          const response = await authApi.login(credentials);
          localStorage.setItem("token", response.token);
          set({
            user: response.user,
            token: response.token,
            isAuthenticated: true,
            loading: false,
            error: null,
          });
        } catch (error) {
          const message =
            error instanceof Error ? error.message : "Login failed";
          set({ loading: false, error: message, isAuthenticated: false });
          throw error;
        }
      },

      register: async (credentials: SignupCredentials) => {
        set({ loading: true, error: null });
        try {
          const response = await authApi.register(credentials);
          localStorage.setItem("token", response.token);
          set({
            user: response.user,
            token: response.token,
            isAuthenticated: true,
            loading: false,
            error: null,
          });
        } catch (error) {
          const message =
            error instanceof Error ? error.message : "Registration failed";
          set({ loading: false, error: message, isAuthenticated: false });
          throw error;
        }
      },

      logout: async () => {
        set({ loading: true });
        try {
          await authApi.logout();
        } finally {
          localStorage.removeItem("token");
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            loading: false,
            error: null,
          });
        }
      },

      getCurrentUser: async () => {
        const token = localStorage.getItem("token");
        if (!token) {
          set({ isAuthenticated: false, user: null, loading: false });
          return;
        }

        set({ loading: true });
        try {
          const user = await authApi.getCurrentUser();
          set({
            user,
            isAuthenticated: true,
            loading: false,
          });
        } catch {
          localStorage.removeItem("token");
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            loading: false,
          });
        }
      },

      loginWithGoogle: async (token: string) => {
        set({ loading: true, error: null });
        try {
          const response = await authApi.loginWithGoogle(token);
          localStorage.setItem("token", response.token);
          set({
            user: response.user,
            token: response.token,
            isAuthenticated: true,
            loading: false,
            error: null,
          });
          return true;
        } catch (error) {
          const message =
            error instanceof Error ? error.message : "Login failed";
          set({ loading: false, error: message, isAuthenticated: false });
          return false;
        }
      },

      clearError: () => set({ error: null }),

      setUser: (user: User | null) => set({ user }),

      hydrateAuth: async () => {
        const token = localStorage.getItem("token");
        if (token) {
          set({ token, loading: true });
          try {
            const user = await authApi.getCurrentUser();
            set({
              user,
              isAuthenticated: true,
              loading: false,
            });
            return true;
          } catch (error) {
            // Only an authentication failure means the token is bad. Treating
            // every error as one signed people out on a transient 429 or a
            // network blip, because the catch cleared the session regardless.
            const status = (error as { status?: number })?.status;
            const isAuthFailure = status === 401 || status === 403;

            if (!isAuthFailure) {
              set({ loading: false });
              return false;
            }

            localStorage.removeItem("token");
            localStorage.removeItem("auth-storage");
            set({
              user: null,
              token: null,
              isAuthenticated: false,
              loading: false,
            });
            return false;
          }
        } else {
          // No token found, set loading to false
          set({ loading: false, isAuthenticated: false });
          return false;
        }
      },
    }),
    {
      name: "auth-storage",
      partialize: (state) => ({
        token: state.token,
        user: state.user,
      }),
      onRehydrateStorage: () => (state) => {
        // After rehydration, trigger hydrateAuth to verify token with backend
        if (state) {
          state.hydrateAuth();
        }
      },
    },
  ),
);
